import { type ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/ds/shadcn/components/ui/chart";
import { cn } from "@/ds/utils";
import React, { useCallback, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

export interface StandardLineChartProps<T> {
	data: T[];
	config: ChartConfig;
	xAxisDataKey: keyof T;
	className?: string;
}

export const StandardLineChart = <T, >(props: StandardLineChartProps<T>): React.JSX.Element => {
	const {
		data,
		config,
		xAxisDataKey,
		className
	} = props;

	const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

	const handleLegendClick = useCallback((dataKey: string) => {
		setHiddenLines(prev => {
			const next = new Set(prev);

			if (next.has(dataKey)) {
				next.delete(dataKey);
			} else {
				next.add(dataKey);
			}

			return next;
		});
	}, []);

	return (
		<ChartContainer config={config} className={cn("min-h-64 max-h-96.5 w-full border-b py-4 pr-4 @md:pr-6", className)}>
			<LineChart accessibilityLayer data={data}>
				<CartesianGrid vertical={true} />
				<XAxis dataKey={xAxisDataKey as string} tickLine={true} axisLine={true} tickMargin={8} fontSize={10} />
				<YAxis tickLine={true} axisLine={true} tickMargin={8} fontSize={10} domain={["auto", "auto"]} />
				<ChartTooltip content={<ChartTooltipContent />} />
				<ChartLegend content={<ChartLegendContent hiddenItems={hiddenLines} onItemClick={handleLegendClick} />} />
				{
					Object.keys(config).map(x => (
						<Line key={x} dataKey={x} type="bump" stroke={`var(--color-${x})`} strokeWidth={2} dot={false} hide={hiddenLines.has(x)} />
					))
				}
			</LineChart>
		</ChartContainer>
	);
};
